import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Phone,
  PhoneOff,
  Video,
  Volume2,
  VolumeX
} from 'lucide-react'
import { getIncomingCallDisplayName, getInitials } from '../../utils/displayName'
import { routeDetectionService } from '../../services/RouteDetectionService'

interface IncomingCallNotificationProps {
  isVisible: boolean
  callType: 'voice' | 'video'
  callerName: string
  callerAvatar?: string | undefined
  callerPeer?: {
    displayName?: string
    alias?: string
    peerId?: string
    id?: string
  }
  onAccept: () => void
  onDecline: () => void
  autoDeclineTimeout?: number // in seconds
  enableAutoNavigation?: boolean // New prop to control auto-navigation
}

export function IncomingCallNotification({
  isVisible,
  callType,
  callerName,
  callerAvatar,
  callerPeer,
  onAccept,
  onDecline,
  autoDeclineTimeout = 30,
  enableAutoNavigation = true
}: IncomingCallNotificationProps) {
  const navigate = useNavigate()
  // CRITICAL FIX: Use the pre-resolved callerName as PRIMARY source
  // (EnhancedChatPage now passes properly resolved name from connection registry)
  const enhancedCallerName = (() => {
    // Priority 1: Use the pre-resolved callerName (now properly resolved in EnhancedChatPage)
    if (callerName && callerName !== 'Unknown User' && callerName !== 'Unknown Caller' && callerName !== 'Unknown') {
      console.log('✅ Using pre-resolved caller name:', callerName)
      return callerName
    }

    // Priority 2: Fallback to callerPeer data
    if (callerPeer && (callerPeer.displayName || callerPeer.alias)) {
      const resolved = getIncomingCallDisplayName(callerPeer)
      if (resolved && resolved !== 'Unknown User' && resolved !== 'Unknown Caller' && resolved !== 'Unknown') {
        console.log('✅ Using callerPeer resolved name:', resolved)
        return resolved
      }
    }

    // Priority 3: Final fallback
    console.log('⚠️ Using fallback: Unknown Caller')
    return 'Unknown Caller'
  })()

  // Generate initials for avatar fallback
  const callerInitials = getInitials(enhancedCallerName)
  const [timeRemaining, setTimeRemaining] = useState(autoDeclineTimeout)
  const [isMuted, setIsMuted] = useState(false)
  const [audioContext] = useState(() => typeof window !== 'undefined' ? new AudioContext() : null)

  // Play ringtone
  useEffect(() => {
    console.log('🔔 RINGTONE DEBUG:', {
      isVisible,
      isMuted,
      audioContext: !!audioContext,
      audioContextState: audioContext?.state,
      userAgent: navigator.userAgent.substring(0, 50)
    })

    if (!isVisible || isMuted || !audioContext) {
      console.log('⚠️ Ringtone skipped:', { isVisible, isMuted, audioContext: !!audioContext })
      return
    }

    let oscillator: OscillatorNode | null = null
    let gainNode: GainNode | null = null
    let isPlaying = true

    const playRingtone = () => {
      if (!isPlaying || !audioContext) return

      // Create call-type specific ringtone pattern
      oscillator = audioContext.createOscillator()
      gainNode = audioContext.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)

      // Different ringtone patterns for voice vs video calls
      const frequencies = callType === 'video'
        ? [392, 523.25, 659.25, 523.25, 0] // G4, C5, E5, C5, silence (video call pattern)
        : [523.25, 659.25, 523.25, 0] // C5, E5, C5, silence (voice call pattern)
      let noteIndex = 0

      const playNote = () => {
        if (!isPlaying || !oscillator || !gainNode) return

        const frequency = frequencies[noteIndex]
        if (frequency === 0 || frequency === undefined) {
          gainNode.gain.value = 0
        } else {
          oscillator.frequency.value = frequency
          gainNode.gain.value = 0.1
        }

        noteIndex = (noteIndex + 1) % frequencies.length

        if (isPlaying) {
          setTimeout(playNote, 250)
        }
      }

      oscillator.type = 'sine'
      oscillator.start()
      playNote()
    }

    // Start playing after a short delay
    const startTimeout = setTimeout(async () => {
      console.log('🔔 Starting ringtone, AudioContext state:', audioContext.state)

      if (audioContext.state === 'suspended') {
        try {
          await audioContext.resume()
          console.log('✅ AudioContext resumed successfully')
        } catch (error) {
          console.error('❌ AudioContext resume failed:', error)
          return
        }
      }

      // EMERGENCY FALLBACK: Try HTML5 audio as backup
      if (audioContext.state !== 'running') {
        console.warn('⚠️ AudioContext not running, trying HTML5 audio fallback')
        try {
          const audio = new Audio()
          audio.volume = 0.1
          // Generate data URL for simple beep
          const beepUrl = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmEcBjiS1++'
          audio.src = beepUrl
          await audio.play()
          console.log('✅ HTML5 audio fallback playing')
        } catch (fallbackError) {
          console.error('❌ HTML5 audio fallback failed:', fallbackError)
        }
      } else {
        playRingtone()
      }
    }, 100)

    return () => {
      isPlaying = false
      clearTimeout(startTimeout)
      if (oscillator) {
        oscillator.stop()
        oscillator.disconnect()
      }
      if (gainNode) {
        gainNode.disconnect()
      }
    }
  }, [isVisible, isMuted, audioContext])

  // Auto-decline timer
  useEffect(() => {
    if (!isVisible) {
      setTimeRemaining(autoDeclineTimeout)
      return
    }

    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          onDecline()
          return autoDeclineTimeout
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [isVisible, autoDeclineTimeout, onDecline])

  // CONTEXTUAL CALL ENHANCEMENT: Auto-navigation logic when incoming call appears
  useEffect(() => {
    if (!isVisible || !enableAutoNavigation) return

    const routeInfo = routeDetectionService.getCurrentRouteInfo()

    // If user is not on chat page, navigate them there for optimal call experience
    if (routeInfo.needsNavigation) {
      console.log('🧭 Incoming call detected on non-chat page, auto-navigating to chat')
      console.log('🧭 Current path:', routeInfo.currentPath)
      console.log('🧭 Navigating to:', routeDetectionService.getCallNavigationTarget())

      // Navigate to chat page
      navigate(routeDetectionService.getCallNavigationTarget())

      // Optional: Show toast notification about navigation
      // This helps users understand why they were navigated
      console.log('📱 Auto-navigated to chat page for incoming call context')
    } else {
      console.log('🧭 User already on chat page, no navigation needed')
    }
  }, [isVisible, enableAutoNavigation, navigate])

  if (!isVisible) return null

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[60] flex items-center justify-center p-4 animate-fadeIn mobile-safe-area">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-auto overflow-hidden animate-slideUp mobile-prevent-overflow">
        {/* Header with gradient */}
        <div className="bg-gradient-to-br from-primary-500 to-primary-600 p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              {callType === 'video' ? (
                <Video className="w-5 h-5" />
              ) : (
                <Phone className="w-5 h-5" />
              )}
              <span className="text-sm font-medium">
                Incoming {callType === 'video' ? 'Video' : 'Voice'} Call
              </span>
            </div>
            <button
              onClick={() => setIsMuted(!isMuted)}
              className="p-2 hover:bg-white/20 rounded transition-colors mobile-touch-target"
              title={isMuted ? 'Unmute ringtone' : 'Mute ringtone'}
            >
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
          </div>

          {/* Caller Info */}
          <div className="flex flex-col items-center">
            {/* Avatar with pulsing ring */}
            <div className="relative mb-4">
              <div className="absolute inset-0 bg-white/30 rounded-full animate-ping" />
              <div className="relative w-24 h-24 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                {callerAvatar ? (
                  <img
                    src={callerAvatar}
                    alt={enhancedCallerName}
                    className="w-20 h-20 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-2xl font-semibold text-white">
                    {callerInitials}
                  </span>
                )}
              </div>
            </div>

            <h3 className="text-xl sm:text-2xl font-semibold mb-2 text-center leading-tight">
              {enhancedCallerName}
            </h3>
            <p className="text-white/80 text-sm text-center">is calling you...</p>
          </div>
        </div>

        {/* Timer */}
        <div className="px-6 py-3 bg-secondary-50 border-b border-secondary-200">
          <div className="flex items-center justify-between">
            <span className="text-sm text-secondary-600">Auto-decline in</span>
            <span className="text-sm font-medium text-secondary-900">{timeRemaining}s</span>
          </div>
          <div className="mt-2 w-full bg-secondary-200 rounded-full h-1 overflow-hidden">
            <div
              className="h-full bg-primary-500 transition-all duration-1000"
              style={{ width: `${(timeRemaining / autoDeclineTimeout) * 100}%` }}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 flex items-center justify-between space-x-4">
          {/* Decline Button */}
          <button
            onClick={onDecline}
            className="flex-1 flex flex-col items-center space-y-2 p-4 bg-red-500 hover:bg-red-600 text-white rounded-xl transition-all transform hover:scale-105 active:scale-95 mobile-touch-target"
          >
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
              <PhoneOff className="w-7 h-7" />
            </div>
            <span className="text-sm font-medium">Decline</span>
          </button>

          {/* Accept Button */}
          <button
            onClick={onAccept}
            className="flex-1 flex flex-col items-center space-y-2 p-4 bg-green-500 hover:bg-green-600 text-white rounded-xl transition-all transform hover:scale-105 active:scale-95 animate-pulse mobile-touch-target"
          >
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
              {callType === 'video' ? (
                <Video className="w-7 h-7" />
              ) : (
                <Phone className="w-7 h-7" />
              )}
            </div>
            <span className="text-sm font-medium">Accept</span>
          </button>
        </div>

        {/* Quick Message Options */}
        <div className="px-6 pb-4">
          <p className="text-xs text-secondary-500 text-center mb-2">Send a quick message:</p>
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => {
                onDecline()
                // Would send message: "Can't talk right now"
              }}
              className="px-3 py-1 text-xs bg-secondary-100 hover:bg-secondary-200 text-secondary-700 rounded-full transition-colors"
            >
              Can't talk right now
            </button>
            <button
              onClick={() => {
                onDecline()
                // Would send message: "Call you back"
              }}
              className="px-3 py-1 text-xs bg-secondary-100 hover:bg-secondary-200 text-secondary-700 rounded-full transition-colors"
            >
              Call you back
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}